import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { resolveMeta, OG_LOCALE } from '../seo/meta';
import { SITE_URL, LANGUAGES, DEFAULT_LANG, localizedUrl, Lang } from '../seo/routes';

interface SeoProps {
  /** Schlüssel der Seite, siehe src/seo/routes.json + src/seo/meta.ts. */
  pageKey: string;
  /** Optionales Social-Sharing-Bild, relativ zur Domain (Default: /og-image.jpg). */
  image?: string;
}

/**
 * Setzt pro Route deklarativ <title>, <meta description>, Open-Graph-, Twitter-,
 * Canonical- und hreflang-Tags – sprachabhängig (aus dem LanguageContext, der die
 * Sprache aus dem URL-Präfix ableitet). Canonical und Alternates werden aus der
 * zentralen Routen-Map (src/seo/routes.json) gebildet, sodass jede Sprachversion
 * korrekt auf /<lang>/<slug> verweist.
 */
const Seo: React.FC<SeoProps> = ({ pageKey, image = '/og-image.jpg' }) => {
  const { language } = useLanguage();

  const meta = resolveMeta(pageKey, language);
  if (!meta) return null;

  const canonical = localizedUrl(pageKey, language);
  const ogLocale = OG_LOCALE[language as Lang] || OG_LOCALE.es;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <html lang={language} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />

      {/* hreflang-Alternates für alle Sprachversionen + x-default (Deutsch) */}
      {LANGUAGES.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={localizedUrl(pageKey, lang)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={localizedUrl(pageKey, DEFAULT_LANG)} />

      {/* Open Graph */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:image" content={imageUrl} />

      {/* Twitter */}
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
};

export default Seo;
